<template>
  <div
    class="h-full"
    :class="{
      'uc-row': showNewDesign,
      'uc-column': !showNewDesign,
      'form-page--borna-auth': isBornaAuth,
      'form-page--design-01': isDesign01,
      'form-page--design-02': isDesign02,
      'form-page--student-onboarding': isStudentOnboarding,
    }"
  >
    <div
      :class="{
        'bg-content uc-column items-center justify-center': showNewDesign,
        FormPageTemplate: !showNewDesign,
      }"
    >
      <!--
        Every Borna auth screen puts the brand lockup INSIDE the card (see
        LoginView / StudentOnboardingWizard), so the shared top-of-page logos
        are suppressed for the whole auth shell. Every other consumer leaves
        `variant` null and renders exactly as before.
      -->
      <img
        v-if="!showNewDesign && !isBornaAuth"
        class="logo-white"
        src="@/assets/header_logo.jpg"
        alt="Borna"
      />
      <img
        v-if="!isBornaAuth"
        :class="{
          'logo-teal-new-design': showNewDesign,
          'logo-teal': !showNewDesign,
        }"
        src="@/assets/header_logo.jpg"
        alt="Borna"
      />
      <slot></slot>
      <img
        v-if="showNewDesign"
        class="img-updog-subjects"
        src="@/assets/updog-subjects.png"
      />
    </div>
    <!--
      Borna auth page shell: pale tinted ground with soft decorative shapes,
      replacing the illustrated UpChieve scene for the auth journey only.
    -->
    <div v-if="isBornaAuth" class="design01-bg" aria-hidden="true">
      <span class="design01-bg__blob design01-bg__blob--one"></span>
      <span class="design01-bg__blob design01-bg__blob--two"></span>
    </div>
    <div v-else-if="!showNewDesign" class="background-img"></div>
    <div v-else class="bg-fixed-container">
      <div class="bg-fixed  uc-column items-center justify-end">
        <h1 class="header">
          {{ $t('verification.formPageTemplate.header') }}
        </h1>
        <div class="image-container">
          <chat-mobile class="w-full h-full" />
        </div>
      </div>
    </div>
    <nav v-if="!isBornaAuth" class="footer" aria-label="More information">
      <div>
        <router-link :to="`/${$i18n.locale}/contact`">{{
          $t('verification.formPageTemplate.contact')
        }}</router-link>
      </div>
      <div>
        <router-link :to="`/${$i18n.locale}/legal`" target="_blank">{{
          $t('verification.formPageTemplate.legal')
        }}</router-link>
      </div>
      <div>
        <a href="https://upchieve.org/" target="_blank">{{
          $t('verification.formPageTemplate.ourWebsite')
        }}</a>
      </div>
    </nav>
    <!--
      TASK-SF-07: the student-onboarding shell states consent ONCE, outside
      the card, with the قوانین/حریم link living here (HUMAN DECISION 3) --
      not duplicated inside any wizard step.
    -->
    <i18n
      v-else-if="isStudentOnboarding"
      path="login.studentOnboarding.footnote"
      tag="p"
      class="design01-footer"
      data-testid="student-onboarding-consent-footer"
    >
      <router-link
        :to="`/${$i18n.locale}/legal`"
        target="_blank"
        class="design01-footer__link"
        data-testid="student-onboarding-consent-link"
      >
        {{ $t('login.studentOnboarding.footnoteLink') }}
      </router-link>
    </i18n>
    <!--
      DESIGN-01 / DESIGN-02 replace the legacy 3-link footer bar (and its
      external upchieve.org link) with a single consent line, per the
      reference.
    -->
    <p v-else class="design01-footer" data-testid="design01-consent-footer">
      {{ $t('login.mobile.consentFooter') }}
    </p>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import ChatMobile from '@/assets/chat-mobile.svg'

export default {
  components: {
    ChatMobile,
  },
  props: {
    // Auth-shell opt-in. LoginView passes 'design-01' at PHONE_ENTRY,
    // 'design-02' at OTP_ENTRY, 'student-onboarding' at STUDENT_ONBOARDING and
    // 'auth' for every other state of the mobile auth journey; every other
    // consumer (verification, signup, reset-password, partner signup) leaves it
    // null and renders byte-identically to before.
    variant: {
      type: String,
      default: null,
    },
  },
  computed: {
    ...mapGetters({
      isNewEligibilityFormDesignEnabled:
        'featureFlags/isNewEligibilityFormDesignEnabled',
    }),
    isDesign01() {
      return this.variant === 'design-01'
    },
    // DESIGN-02 (OTP) reuses the DESIGN-01 Borna page shell -- tinted ground,
    // decorative orbs, consent footnote, no top-of-page UpChieve logo -- while
    // LoginView supplies its own in-card composition. Only present when
    // LoginView passes `variant="design-02"` at OTP_ENTRY.
    isDesign02() {
      return this.variant === 'design-02'
    },
    // TASK-SF-07: the dedicated student-onboarding shell -- same pale Borna
    // canvas + decorative orbs as DESIGN-01, but activated explicitly by its
    // own variant string (LoginView passes it only at STUDENT_ONBOARDING)
    // and carrying its own consent footnote with the قوانین/حریم link.
    isStudentOnboarding() {
      return this.variant === 'student-onboarding'
    },
    // WP-04V-B round 2. THE auth shell. Human visual UAT round 1 failed because
    // only three states opted in: role selection, registration, account
    // conflict and role onboarding fell through to the legacy composition
    // (illustrated UpChieve scene, `header_logo.jpg`, 3-link grey footer bar,
    // legacy base type) in the middle of an otherwise Borna journey. The shell
    // is now one thing that the whole journey shares, and the three named
    // variants only add screen-specific composition on top of it.
    isBornaAuth() {
      return (
        this.isDesign01 ||
        this.isDesign02 ||
        this.isStudentOnboarding ||
        this.variant === 'auth'
      )
    },
    showNewDesign() {
      return (
        this.isNewEligibilityFormDesignEnabled &&
        this.$route.path.includes('eligibility') &&
        this.$route.query['partner'] === 'bigfuture'
      )
    },
  },
}
</script>

<style lang="scss" scoped>
// ---------------------------------------------------------------------------
// The Borna auth page shell. Every rule is nested under the single
// `.form-page--borna-auth` class, present only when LoginView passes one of the
// auth variants, so no other form screen (verification, signup, reset-password,
// partner signup) is touched.
// ---------------------------------------------------------------------------
.form-page--borna-auth {
  // Prototype `.auth-page`: flat #f5f7fc ground with a soft indigo tint from
  // the top-left corner and a soft cyan tint from the bottom-right.
  background: radial-gradient(circle at 18% 20%, #314bd617, transparent 28%),
    radial-gradient(circle at 84% 84%, #14b7d71a, transparent 30%), #f5f7fc;
  // The card and the consent line read as one vertically-centred group -- the
  // footnote follows the card with a modest gap, never pinned to the viewport
  // floor.
  justify-content: center;
  min-height: 100vh;
  overflow-x: hidden;
  position: relative;

  .FormPageTemplate {
    // Not `flex: 1 1 auto` -- that stretched this block to fill the column and
    // pushed the consent line to the very bottom.
    flex: 0 0 auto;
    justify-content: flex-start;
    padding: 40px 16px 0;
    position: relative;
    z-index: 1;

    @include breakpoint-below('tiny') {
      // WP-04V-B round 2: the shared `.FormPageTemplate` rule paints this block
      // WHITE below 'tiny' (it is the page surface on the legacy mobile form).
      // On the Borna shell that put an opaque white rectangle between the
      // tinted canvas and the card, so the decorative ground stopped at the
      // card's edge on every auth screen at 390px. The shell owns the canvas
      // here, so this block paints nothing.
      background-color: transparent;
      // Keep page-side spacing on mobile so the card stays a distinct rounded
      // card, not edge-to-edge (prototype `.auth-page` mobile side padding).
      // WP-04V-B: measured 22px at 390px, which puts the card at 346px wide --
      // this shipped as 20px/350px. The 58px top lands the card at y=82, as
      // measured on the prototype at 390x844 (24px of it is the page's own
      // `padding-top` below).
      padding: 58px 22px 0;
    }
  }

  // On a phone the prototype anchors the card near the top rather than
  // centring the short group in a tall viewport.
  @include breakpoint-below('tiny') {
    justify-content: flex-start;
    padding-top: 24px;
  }
}

.design01-bg {
  inset: 0;
  overflow: hidden;
  pointer-events: none;
  position: fixed;
  z-index: 0;
}

// Prototype `.auth-orb`. Physical corners -- NOT mirrored by text direction:
// `top/left/right/bottom` are physical properties, unaffected by `direction`.
.design01-bg__blob {
  border-radius: 50%;
  display: block;
  filter: blur(1px);
  position: absolute;

  // `.auth-orb--one`: pale indigo, TOP-LEFT.
  &--one {
    background: #dbe4ff;
    height: 240px;
    left: -100px;
    top: -80px;
    width: 240px;
  }

  // `.auth-orb--two`: pale cyan/turquoise, BOTTOM-RIGHT.
  &--two {
    background: #cdf1f8;
    bottom: -110px;
    height: 280px;
    right: -150px;
    width: 280px;
  }
}

// WP-04V-B round 2: measured `rgb(101,113,141)` on the prototype's
// `.auth-footnote`; this shipped as `$c-secondary-grey` (#77778b).
.design01-footer {
  color: $borna-muted;
  font-size: 11px;
  // Modest gap under the card (prototype `.auth-footnote` margin-top: 20px),
  // then normal page breathing room below.
  margin: 20px 0 0;
  padding: 0 16px 32px;
  position: relative;
  text-align: center;
  width: 100%;
  z-index: 1;
}

// TASK-SF-07: the قوانین/حریم link inside the student-onboarding footnote.
.design01-footer__link {
  color: $c-information-blue;
  text-decoration: underline;

  &:hover {
    color: $c-information-blue;
  }
}

.bg-fixed-container {
  flex: 1 1 0;
  height: 100%;

  @include breakpoint-below('tiny') {
    display: none;
  }
}

.bg-fixed {
  background-color: #f0f9ff;
  height: 100%;
  position: fixed;
  width: 50%;
}

.header {
  font-size: 30px;
  font-weight: 600;
  max-width: 65%;
  min-width: 300px;
  padding: 16px 24px;
  text-align: center;
}

.image-container {
  height: 100%;
  max-height: 70vh;
  width: 100%;
}

.bg-content {
  background-color: white;
  flex: 1 1 0;
  margin: auto;
  min-height: 100vh;
  padding-bottom: 50px;

  .uc-form::v-deep {
    padding-top: 0px;

    @include breakpoint-below('tiny') {
      padding-bottom: 0px;
    }
  }

  @include breakpoint-below('tiny') {
    padding-bottom: 100px;
  }
}

.logo-teal-new-design {
  margin: 16px;
}

.img-updog-subjects {
  margin-top: 10px;
  @include breakpoint-above('tiny') {
    display: none;
  }
}

/* old */
.background-img {
  background-color: white;

  @include breakpoint-above('tiny') {
    background: url('~@/assets/onboarding_background.png') no-repeat center
      center;
    background-attachment: fixed;
    background-size: cover;
    height: 100%;
    left: 0;
    position: fixed;
    top: 0;
    width: 100%;
    z-index: -1;
  }
}

.FormPageTemplate {
  @include flex-container(column, flex-start, center);
  padding-bottom: 60px;

  @include breakpoint-below('tiny') {
    background-color: white;
  }
}

// Task 12 / STEP-06 (D). The mark read as an afterthought above the auth card:
// 95px on mobile and 155px on desktop, with almost no breathing room, against
// a 500px-wide card. Enlarged and given deliberate spacing so the logo and the
// card read as one composition.
//
// Size only -- the artwork itself is untouched, and the source asset is
// 1280x618, so neither size is anywhere near its intrinsic resolution and no
// blurring is introduced.
.logo-white {
  max-width: 190px;
  margin-bottom: 18px;
  margin-top: 18px;
  display: none;

  @include breakpoint-above('tiny') {
    display: block;
  }
}

.logo-teal {
  display: block;
  max-width: 130px;
  padding-top: 20px;
  padding-bottom: 8px;

  @include breakpoint-above('tiny') {
    display: none;
  }
}

.footer {
  @include flex-container(column, space-around, center);

  background-color: #f6f6f6;
  border-top: 1px solid #cccccf;

  font-size: 12px;
  font-weight: 600;

  min-height: 100px;
  margin-top: auto;
  width: 100%;

  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;

  a {
    color: #73737a;
    text-transform: uppercase;
  }

  @include breakpoint-above('tiny') {
    flex-direction: row;
    min-height: 50px;
  }
}

html.is-rtl {
  .background-img {
    @include breakpoint-above('tiny') {
      right: 0;
      left: unset;
    }
  }
}
</style>
